import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Clock, CheckCircle, Lock, Play, Pause, BookOpen, 
  FileText, MessageSquare, ChevronRight, AlertCircle,
  Timer, Users, Video
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Format seconds to HH:MM:SS
const formatTime = (seconds) => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

// Format seconds to human readable
const formatTimeHuman = (seconds) => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) {
    return `${h} Std ${m} Min`;
  }
  return `${m} Min`;
};

// Learning Timer Component - sends heartbeats to backend
export const LearningTimer = ({ 
  moduleId, 
  courseId, 
  getAuthHeader, 
  onTimeCompleted,
  requiredSeconds = 0,
  initialAccumulated = 0 
}) => {
  const [isActive, setIsActive] = useState(true);
  const [accumulatedSeconds, setAccumulatedSeconds] = useState(initialAccumulated);
  const [isVisible, setIsVisible] = useState(true);
  const intervalRef = useRef(null);
  const heartbeatRef = useRef(null);

  // Handle visibility change - pause when tab is not visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      const visible = document.visibilityState === 'visible';
      setIsVisible(visible);
      if (!visible) {
        setIsActive(false);
        toast.info('⏸️ Timer pausiert - Tab nicht aktiv', { duration: 2000 });
      } else {
        setIsActive(true);
        toast.success('▶️ Timer läuft weiter', { duration: 2000 });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Local timer display (updates every second)
  useEffect(() => {
    if (isActive && isVisible) {
      intervalRef.current = setInterval(() => {
        setAccumulatedSeconds(prev => prev + 1);
      }, 1000);
    } else {
      clearInterval(intervalRef.current);
    }
    return () => clearInterval(intervalRef.current);
  }, [isActive, isVisible]);

  // Heartbeat to backend (every 30 seconds)
  useEffect(() => {
    const sendHeartbeat = async () => {
      if (!isActive || !isVisible) return;
      
      try {
        const res = await axios.post(`${API}/api/learning/heartbeat`, {
          module_id: moduleId,
          course_id: courseId
        }, getAuthHeader());
        
        // Sync with server time
        setAccumulatedSeconds(res.data.accumulated_seconds);
        
        if (res.data.time_completed && onTimeCompleted) {
          onTimeCompleted();
        }
      } catch (error) {
        console.error('Heartbeat error:', error);
      }
    };

    // Send heartbeat every 30 seconds
    heartbeatRef.current = setInterval(sendHeartbeat, 30000);
    
    // Send initial heartbeat
    sendHeartbeat();
    
    return () => clearInterval(heartbeatRef.current);
  }, [moduleId, courseId, isActive, isVisible, getAuthHeader, onTimeCompleted]);

  const remainingSeconds = Math.max(0, requiredSeconds - accumulatedSeconds);
  const progressPercent = requiredSeconds > 0 
    ? Math.min(100, (accumulatedSeconds / requiredSeconds) * 100) 
    : 100;
  const isCompleted = accumulatedSeconds >= requiredSeconds;

  return (
    <div data-testid="learning-timer">
      <div className="flex items-baseline justify-between gap-4 flex-wrap mb-2">
        <span className="font-mono text-[11px] uppercase font-semibold" style={{ letterSpacing: '0.2em', color: '#6B6157' }}>
          Aktive Lernzeit {formatTime(accumulatedSeconds)}
          <span style={{ color: '#8F650F' }}> · </span>
          Empfohlen {formatTimeHuman(requiredSeconds)}
        </span>
        {isCompleted ? (
          <span className="font-display italic text-[15px] leading-none" style={{ color: '#8F650F' }}>✓ erfüllt.</span>
        ) : (
          <span className="font-display italic text-[15px] text-terra leading-none">
            {isActive ? 'läuft.' : 'pausiert.'}
          </span>
        )}
      </div>
      <div className="h-[2px] w-full rounded-full overflow-hidden" style={{ backgroundColor: '#E0D6CA' }}>
        <div
          className="h-full"
          style={{ width: `${progressPercent}%`, backgroundColor: '#D89232', transition: 'width 0.5s ease-out' }}
        />
      </div>
      {!isVisible && (
        <p className="font-display italic text-[13px] text-sand-600 mt-2">
          <span style={{ color: '#8F650F' }}>✳</span> Timer pausiert — bleib auf dieser Seite, damit deine Lernzeit zählt.
        </p>
      )}
    </div>
  );
};

// Task Component - for displaying and submitting tasks
export const TaskItem = ({ 
  task, 
  moduleId,
  getAuthHeader,
  onSubmitted 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [answer, setAnswer] = useState(task.submission?.answer_text || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const wordCount = answer.trim().split(/\s+/).filter(w => w).length;

  const typeLabels = {
    'fallbeispiel': { label: 'Fallbeispiel', color: 'bg-terra/15 text-terra-dark', icon: FileText },
    'reflexion': { label: 'Reflexion', color: 'bg-peach-100 text-peach-dark', icon: MessageSquare },
    'freitext': { label: 'Übung', color: 'bg-sage/15 text-sage-dark', icon: BookOpen },
    'uebung': { label: 'Übung', color: 'bg-sage/15 text-sage-dark', icon: BookOpen }
  };

  const typeInfo = typeLabels[task.type] || typeLabels['freitext'];
  const TypeIcon = typeInfo.icon;

  const handleSubmit = async () => {
    // Soft-Nudge statt harter Block: bei zu kurzer Antwort kurzer Confirm-Dialog,
    // aber die Abgabe wird beim "OK" trotzdem gespeichert. Verhindert das alte Problem,
    // dass User dachten sie hätten abgegeben, der Server aber 400 zurückgab.
    if (wordCount === 0) {
      toast.error('Bitte schreibe eine Antwort, bevor du speicherst.');
      return;
    }
    if (task.min_words > 0 && wordCount < task.min_words) {
      const ok = window.confirm(
        `Deine Antwort hat nur ${wordCount} Wörter. Empfohlen sind mind. ${task.min_words}.\n\n` +
        `Trotzdem speichern? (Du kannst die Antwort jederzeit erweitern.)`
      );
      if (!ok) return;
    }

    setIsSubmitting(true);
    try {
      const res = await axios.post(`${API}/api/learning/tasks/${moduleId}/submit`, {
        task_id: task.id,
        answer_text: answer
      }, getAuthHeader());
      
      if (res.data?.meets_min_words === false) {
        toast.warning(res.data.message || 'Antwort gespeichert (kürzer als empfohlen)');
      } else {
        toast.success('Aufgabe gespeichert!');
      }
      if (onSubmitted) onSubmitted();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    }
    setIsSubmitting(false);
  };

  return (
    <div className="rounded-[4px] border transition-colors" style={{ borderColor: task.submitted ? 'rgba(216,146,50,0.5)' : '#E0D6CA', backgroundColor: '#F5F0EA' }}>
      {/* Header */}
      <div 
        className="p-4 sm:p-5 cursor-pointer flex items-center justify-between gap-3"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="min-w-0">
          <p className="font-mono text-[10px] uppercase font-semibold mb-1.5" style={{ letterSpacing: '0.22em', color: '#8F650F' }}>
            {typeInfo.label}{task.min_words > 0 && ` · Empfohlen ~${task.min_words} Wörter`}
            {task.submitted && <span className="text-terra"> · Eingereicht</span>}
          </p>
          <h4 className="font-display text-xl text-espresso leading-snug">{task.title}</h4>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {task.submitted && <CheckCircle className="w-4 h-4" style={{ color: '#8F650F' }} strokeWidth={2} />}
          <ChevronRight className={`w-5 h-5 text-sand-500 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-4 sm:px-5 pb-5 border-t" style={{ borderColor: '#E0D6CA' }}>
          {/* Task Description */}
          <div className="mt-4 py-4 pl-5 pr-3" style={{ borderLeft: '2px solid #D89232' }}>
            <h5 className="font-mono text-[10.5px] uppercase font-semibold text-terra mb-3" style={{ letterSpacing: '0.2em' }}>Aufgabenstellung</h5>
            <div>{renderTaskDescription(task.description)}</div>
          </div>

          {/* Answer Area */}
          <div className="mt-5">
            <div className="flex justify-between items-center mb-2">
              <label className="text-[11px] uppercase font-semibold" style={{ letterSpacing: '0.18em', color: '#6B6157' }}>Deine Antwort</label>
              <span className="font-mono text-[10.5px] uppercase" style={{ letterSpacing: '0.12em', color: wordCount >= (task.min_words || 0) ? '#D89232' : '#A89A88' }}>
                {wordCount}{task.min_words ? ` von ~${task.min_words}` : ''} Wörter{wordCount >= (task.min_words || 0) && task.min_words > 0 ? ' ✓' : ''}
              </span>
            </div>
            <textarea
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              className="w-full h-64 rounded-[4px] p-4 text-sand-800 focus:outline-none resize-y transition-colors"
              style={{ backgroundColor: '#FDFBF8', border: '1px solid #E0D6CA' }}
              onFocus={(e) => { e.currentTarget.style.borderColor = '#E07A5F'; }}
              onBlur={(e) => { e.currentTarget.style.borderColor = '#E0D6CA'; }}
              placeholder="Schreib hier deine ausführliche Antwort…"
              data-testid={`task-answer-textarea-${task.id}`}
            />
          </div>

          {/* Submit Button — Terra-Outline-Pill */}
          <div className="mt-4 flex justify-end">
            <button
              onClick={handleSubmit}
              disabled={isSubmitting || wordCount === 0}
              className="h-11 px-8 rounded-full bg-transparent text-[12px] font-bold uppercase transition-colors disabled:opacity-40"
              style={{ border: '1.5px solid #E07A5F', color: '#E07A5F', letterSpacing: '0.18em' }}
              onMouseEnter={(e) => { if (!e.currentTarget.disabled) { e.currentTarget.style.backgroundColor = '#E07A5F'; e.currentTarget.style.color = '#F5F0EA'; } }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#E07A5F'; }}
              data-testid={`submit-task-${task.id}`}
            >
              {isSubmitting ? 'Wird gespeichert…' : task.submitted ? 'Aktualisieren' : 'Absenden'}
            </button>
          </div>

          {/* Feedback from Admin */}
          {task.submission?.admin_feedback && (
            <div className="mt-4 py-4 pl-5 pr-3" style={{ backgroundColor: '#EDE6DC', borderLeft: '2px solid #D89232' }}>
              <h5 className="font-display italic text-[16px] text-terra mb-1.5">Feedback vom Dozenten:</h5>
              <p className="text-sand-800 text-sm leading-relaxed">{task.submission.admin_feedback}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Module Status Card - shows all requirements for a module
export const ModuleStatusCard = ({ 
  status,
  onStartLearning 
}) => {
  const requirements = [
    {
      label: 'Lernzeit',
      completed: status.time_completed,
      detail: `${formatTimeHuman(status.accumulated_seconds)} / ${formatTimeHuman(status.required_seconds)}`,
      icon: Clock
    },
    {
      label: 'Aufgaben',
      completed: status.tasks_submitted >= status.tasks_total && status.tasks_total > 0,
      detail: `${status.tasks_submitted} / ${status.tasks_total}`,
      icon: FileText
    },
    {
      label: 'Wissenstest',
      completed: status.quiz_passed,
      detail: status.quiz_passed ? `${status.quiz_score}%` : (status.quiz_unlocked ? 'Bereit' : 'Gesperrt'),
      icon: BookOpen,
      locked: !status.quiz_unlocked
    },
    {
      label: 'Live-Session',
      completed: status.live_session_approved,
      detail: status.live_session_approved ? 'Bestätigt' : 'Ausstehend',
      icon: Users
    }
  ];

  return (
    <div className="pt-2" data-testid="module-status-card">
      <div className="flex items-baseline justify-between mb-4">
        <h3 className="font-display text-[22px] text-espresso">Dein <span className="italic text-terra">Stand</span>.</h3>
        {status.is_completed ? (
          <span className="font-display italic text-[16px]" style={{ color: '#8F650F' }}>✓ Abgeschlossen.</span>
        ) : !status.is_unlocked ? (
          <span className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-sand-500 flex items-center gap-1.5">
            <Lock className="w-3.5 h-3.5" /> Gesperrt
          </span>
        ) : (
          <span className="font-display italic text-[16px] text-terra">In Arbeit.</span>
        )}
      </div>

      <div className="border-t" style={{ borderColor: '#E0D6CA' }}>
        {requirements.map((req, idx) => (
          <div key={idx} className="flex items-center justify-between py-3.5 border-b" style={{ borderColor: '#E0D6CA' }}>
            <span className="font-mono text-[11px] uppercase font-semibold flex items-center gap-2" style={{ letterSpacing: '0.2em', color: req.completed ? '#D89232' : req.locked ? '#A89A88' : '#6B6157' }}>
              {req.completed && <CheckCircle className="w-3.5 h-3.5" style={{ color: '#8F650F' }} />}
              {req.locked && <Lock className="w-3.5 h-3.5" />}
              {req.label}
            </span>
            <span className="font-display text-[17px]" style={{ color: req.completed ? '#D89232' : '#1B1A18' }}>
              {req.detail}
            </span>
          </div>
        ))}
      </div>

      {!status.is_completed && status.is_unlocked && (
        <button
          onClick={onStartLearning}
          className="mt-6 h-[46px] px-10 rounded-full bg-transparent text-[12px] font-bold uppercase transition-colors inline-flex items-center gap-2"
          style={{ border: '1.5px solid #E07A5F', color: '#E07A5F', letterSpacing: '0.2em' }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#E07A5F'; e.currentTarget.style.color = '#F5F0EA'; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#E07A5F'; }}
        >
          Weiterlesen →
        </button>
      )}

      {!status.is_unlocked && (
        <p className="font-display italic text-[14px] text-sand-600 mt-5">
          <span style={{ color: '#8F650F' }}>✳</span> Schließ zuerst das vorherige Kapitel ab.
        </p>
      )}
    </div>
  );
};

// Minimal-Markdown für Aufgabentexte (fett, Listen, Abschnitts-Labels)
const mdInline = (s) =>
  s.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>').replace(/\*(?!\s)(.+?)\*/g, '<em>$1</em>');

const renderTaskDescription = (text) => {
  const lines = (text || '').split('\n');
  let firstPara = true;
  return lines.map((raw, i) => {
    const line = raw.trim();
    if (!line) return <div key={i} className="h-2" />;
    const boldLine = line.match(/^\*\*(.+?)\*\*:?\s*$/);
    if (boldLine) {
      return (
        <h6 key={i} className="font-mono text-[10.5px] uppercase font-semibold mt-4 mb-1.5" style={{ letterSpacing: '0.2em', color: '#8F650F' }}>
          {boldLine[1].replace(/:$/, '')}
        </h6>
      );
    }
    if (/^[-•*]\s+/.test(line)) {
      return (
        <div key={i} className="flex items-start gap-2.5 my-1">
          <span className="mt-[7px] w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: '#D89232' }} />
          <span className="text-sand-800 text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: mdInline(line.replace(/^[-•*]\s+/, '')) }} />
        </div>
      );
    }
    if (firstPara) {
      firstPara = false;
      return (
        <p key={i} className="font-display italic text-[17px] text-espresso leading-relaxed mb-2" dangerouslySetInnerHTML={{ __html: mdInline(line) }} />
      );
    }
    return <p key={i} className="text-sand-800 text-sm leading-relaxed my-1.5" dangerouslySetInnerHTML={{ __html: mdInline(line) }} />;
  });
};

// Tasks List Component
export const TasksList = ({ 
  moduleId, 
  getAuthHeader,
  onAllTasksCompleted 
}) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [moduleInfo, setModuleInfo] = useState({});
  // Refs statt Deps: verhindert Endlos-Schleife durch neue Callback-Identitäten
  // (Toast "Alle Aufgaben erledigt!" feuerte sonst in Dauerschleife)
  const onCompletedRef = useRef(onAllTasksCompleted);
  useEffect(() => { onCompletedRef.current = onAllTasksCompleted; });
  const authRef = useRef(getAuthHeader);
  useEffect(() => { authRef.current = getAuthHeader; });

  const loadTasks = useCallback(async (fromSubmit = false) => {
    try {
      const res = await axios.get(`${API}/api/learning/tasks/${moduleId}`, authRef.current());
      setTasks(res.data.tasks || []);
      setModuleInfo({
        title: res.data.module_title,
        ue: res.data.ue,
        total: res.data.total_tasks,
        submitted: res.data.submitted_tasks
      });
      
      // Nur nach einer echten Abgabe melden — nicht beim bloßen Öffnen des Tabs
      if (fromSubmit && res.data.submitted_tasks >= res.data.total_tasks && res.data.total_tasks > 0) {
        if (onCompletedRef.current) onCompletedRef.current();
      }
    } catch (error) {
      console.error('Error loading tasks:', error);
    }
    setLoading(false);
  }, [moduleId]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-terra" />
      </div>
    );
  }

  if (tasks.length === 0) {
    return (
      <div className="text-center py-12 bg-cream-100 rounded-2xl border border-dashed border-cream-300">
        <FileText className="w-10 h-10 mx-auto mb-3 text-sand-400" strokeWidth={1.5} />
        <p className="text-sand-600 text-sm">Keine Aufgaben für dieses Modul</p>
      </div>
    );
  }

  const allDone = moduleInfo.submitted >= moduleInfo.total;

  return (
    <div className="space-y-4">
      <div className="mb-5">
        <p className="text-[11px] uppercase font-bold text-terra mb-1.5" style={{ letterSpacing: '0.25em' }}>
          Deine Aufgaben · {moduleInfo.submitted} von {moduleInfo.total}
        </p>
        <h3 className="font-display text-[26px] text-espresso leading-snug">
          Zeig, was du damit <span className="italic text-terra">machst</span>.
        </h3>
        {allDone && (
          <p className="font-display italic text-[15px] mt-1" style={{ color: '#8F650F' }}>✓ Alles eingereicht.</p>
        )}
      </div>

      {tasks.map((task) => (
        <TaskItem
          key={task.id}
          task={task}
          moduleId={moduleId}
          getAuthHeader={getAuthHeader}
          onSubmitted={() => loadTasks(true)}
        />
      ))}
    </div>
  );
};

export default {
  LearningTimer,
  TaskItem,
  TasksList,
  ModuleStatusCard
};
